import * as Yup from 'yup';
import { useApiGet, useApiPost } from '../../../services/tanstackApiService';

// ---------------------------------------------------------------------
// Form values, initial values & validation schema for NewPatientPopUp
// ---------------------------------------------------------------------


export const initialNewPatientValues: NewPatientCreatePayload = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  gender: null,
  mobile_number_country_code: '',
  mobile_number: '',
  referral_doctor_id: null,
  referral_doctor_name: '',
};

export const newPatientValidationSchema = Yup.object().shape({
  first_name: Yup.string().trim().required('First name is required'),
  last_name: Yup.string().trim().required('Last name is required'),
  date_of_birth: Yup.string().required('Date of birth is required'),
  gender: Yup.string().required('Gender is required'),
  mobile_number_country_code: Yup.string().trim().required('Country code is required'),
  mobile_number: Yup.string()
    .trim()
    .required('Mobile number is required')
    .matches(/^[0-9]{7,15}$/, 'Enter a valid mobile number'),
  referral_doctor_name: Yup.string(),
});

// ---------------------------------------------------------------------
// Payload type for creating a new patient from this popup
// ---------------------------------------------------------------------

export interface NewPatientCreatePayload {
  first_name: string;
  last_name: string;
  date_of_birth: string; // ISO date string (YYYY-MM-DD)
  gender: number | null; // gender id
  mobile_number: string;
  mobile_number_country_code: string; // e.g. "+91"
  referral_doctor_id: number | null;
  referral_doctor_name: string;
}

// ---------------------------------------------------------------------
// Gender master API types (for dropdown population)
// ---------------------------------------------------------------------

export interface GenderTranslationMap {
  en: string;
  es: string;
  hi: string;
  pt: string;
}

export interface GenderMasterItem {
  id: number;
  name_label: string;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
  deleted_reason: string | null;
  name: string;
  is_active: boolean;
  translations: GenderTranslationMap;
  tenant: number;
}

export interface GenderMasterResponseMeta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
}

export interface GenderMasterResponse {
  meta: GenderMasterResponseMeta;
  objects: GenderMasterItem[];
}


export interface CountryDialCodeItem {
  name: string;
  dial_code: string;
}

export interface CountryDialCodeResponse {
  error: boolean;
  msg: string;
  data: CountryDialCodeItem[];
}

export interface ReferralDoctorDepartment {
  id: number;
  name: string;
  clinic__id: number;
  clinic__name: string;
  name_label: string;
  tenant__name: string;
  is_clinical: boolean;
}

export interface ReferralDoctorItem {
  id: number;
  email: string;
  first_name: string;
  gender__name: string | null;
  last_name: string;
  username: string;
  role: string;
  departments: ReferralDoctorDepartment[];
  appointment_type: string | null;
  is_active: boolean;
  user_type: string | null;
  language: number | null;
  language_code: string | null;
  language_name: string | null;
  is_locked: boolean;
  gender__name_label: string | null;
  role_label: string | null;
}

export interface ReferralDoctorResponseMeta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
}

export interface ReferralDoctorResponse {
  meta: ReferralDoctorResponseMeta;
  objects: ReferralDoctorItem[];
}

export function getGendersURL(): string {
  return 'genders/';
}
export function getCountryCodesURL(): string {
  return 'countries/codes/';
}
export function getReferralDrsURL(): string {
  return 'users/?role=DOCTOR';
}
export function registerPatientURL(): string {
  return 'pharmacy-invoice/register-walkin-patient/';
}

// Mutation hook to register a new walk-in patient from the pharmacy popup
export function useRegisterPatient() {
  return useApiPost<unknown, NewPatientCreatePayload>();
}
export interface ExistingPatientsResponse {
  status: boolean;
  message: string;
  data: {
    objects: Array<{
      patient_id: number;
      appointment_id: number | null;
      drug_id: unknown[];
      date: string;
      patient_name: string;
      age: number;
      provider: string | null;
      prescription_status: string;
      medical_record_no: string;
      gender: string;
    }>;
    meta: {
      limit: number;
      next: string | null;
      offset: number;
      previous: string | null;
      total_count: number;
    };
    counts: {
      total: number;
      ordered: number;
      unordered: number;
      all_checked_in: number;
    };
  };
}

// ---------------------------------------------------------------------
// Combined hook to load all masters for New Patient popup
// ---------------------------------------------------------------------

export function useNewPatientMasters() {
  const gendersQuery = useApiGet<GenderMasterResponse>(
    ['new-patient', 'genders'],
    { path: getGendersURL(), query: { limit: 100, offset: 0 } },
    { staleTime: 5 * 60 * 1000 }
  );

  const countryCodesQuery = useApiGet<CountryDialCodeResponse>(
    ['new-patient', 'country-codes'],
    { path: getCountryCodesURL() },
    { staleTime: 60 * 60 * 1000 }
  );

  const referralDoctorsQuery = useApiGet<ReferralDoctorResponse>(
    ['new-patient', 'referral-doctors'],
    { path: getReferralDrsURL() },
    { staleTime: 5 * 60 * 1000 }
  );

  const isLoading =
    gendersQuery.isLoading ||
    countryCodesQuery.isLoading ||
    referralDoctorsQuery.isLoading;

  const isError =
    gendersQuery.isError ||
    countryCodesQuery.isError ||
    referralDoctorsQuery.isError;

  return {
    gendersQuery,
    countryCodesQuery,
    referralDoctorsQuery,
    genders: gendersQuery.data?.objects ?? [],
    countryCodes: countryCodesQuery.data?.data ?? [],
    referralDoctors: referralDoctorsQuery.data?.objects ?? [],
    isLoading,
    isError,
  };
}


export function getExistingPatient(clinicId:number |string, search:string): string {
  return `pharmacy-invoice/?clinic_id=${clinicId}&search_existing_patient=${search}`;
}


